import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutlineRounded";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import PersonOutlineRoundedIcon from "@material-ui/icons/PersonOutlineRounded";
import TuneRoundedIcon from "@material-ui/icons/TuneRounded";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import PhoneInputWithCountry from "../PhoneInputWithCountry";
import formatContactIdentifier, {
  isBsuidNumber,
} from "../../helpers/formatContactIdentifier";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const surface = isDark ? "#131614" : "#ffffff";
  const subtle = isDark ? "rgba(255,255,255,0.04)" : "#f6faf7";
  const border = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
  const textPrimary = isDark ? "#e6f7ec" : "#102b1f";
  const textMuted = isDark ? "rgba(230,247,236,0.55)" : "#5f6f6a";
  return {
    paper: {
      borderRadius: 16,
      overflow: "hidden",
      background: surface,
      [theme.breakpoints.down("xs")]: { borderRadius: 0 },
    },
    header: {
      position: "relative",
      padding: "18px 22px",
      borderBottom: `1px solid ${border}`,
      background: surface,
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        insetInlineStart: 0,
        insetInlineEnd: 0,
        height: 2,
        background:
          "linear-gradient(90deg, #25d366 0%, #128c7e 60%, transparent 100%)",
        opacity: isDark ? 0.55 : 0.7,
      },
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    title: {
      fontSize: "1.05rem",
      fontWeight: 700,
      color: textPrimary,
      letterSpacing: 0.2,
      margin: 0,
    },
    closeBtn: {
      color: textMuted,
      padding: 6,
    },
    body: {
      padding: "8px 22px 22px",
      maxHeight: "70vh",
      overflowY: "auto",
      [theme.breakpoints.down("xs")]: { maxHeight: "calc(100dvh - 140px)" },
    },
    section: {
      marginTop: 18,
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 10,
      "& svg": {
        fontSize: 18,
        color: isDark ? "#2ee37a" : "#128c7e",
      },
    },
    sectionLabel: {
      fontSize: "0.72rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: textMuted,
    },
    fieldStack: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    field: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        background: subtle,
        transition: "background 120ms ease, border-color 120ms ease",
        "& fieldset": {
          borderColor: border,
        },
        "&:hover fieldset": {
          borderColor: isDark ? "rgba(46,227,122,0.3)" : "rgba(18,140,126,0.35)",
        },
        "&.Mui-focused fieldset": {
          borderColor: isDark ? "#2ee37a" : "#128c7e",
          borderWidth: 1.5,
        },
      },
      "& .MuiInputLabel-outlined": {
        color: textMuted,
      },
      "& .MuiInputLabel-outlined.Mui-focused": {
        color: isDark ? "#2ee37a" : "#128c7e",
      },
      "& .MuiOutlinedInput-input": {
        padding: "13px 14px",
        fontSize: 14,
        color: textPrimary,
      },
      "& .MuiFormHelperText-root": {
        marginInlineStart: 4,
      },
    },
    usernamePrefix: {
      color: isDark ? "#2ee37a" : "#128c7e",
      fontWeight: 700,
      marginInlineEnd: 4,
      fontSize: 15,
    },
    toggleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "12px 14px",
      background: subtle,
      border: `1px solid ${border}`,
      borderRadius: 10,
    },
    toggleLabel: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    toggleTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: textPrimary,
    },
    toggleHint: {
      fontSize: 12,
      color: textMuted,
    },
    extraRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      // Stack the name/value fields on phones so two labeled outlined inputs
      // don't cram into ~136px each on a narrow screen.
      [theme.breakpoints.down("xs")]: { flexDirection: "column", alignItems: "stretch" },
    },
    extraField: { flex: 1 },
    extraDeleteBtn: {
      color: textMuted,
      marginTop: 4,
      "&:hover": { color: "#e53935" },
      [theme.breakpoints.down("xs")]: { alignSelf: "flex-end", marginTop: 0 },
    },
    addExtraBtn: {
      alignSelf: "flex-start",
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 600,
      color: isDark ? "#2ee37a" : "#128c7e",
      borderColor: isDark
        ? "rgba(46,227,122,0.35)"
        : "rgba(18,140,126,0.35)",
      "&:hover": {
        borderColor: isDark ? "#2ee37a" : "#128c7e",
        background: isDark ? "rgba(46,227,122,0.06)" : "rgba(18,140,126,0.04)",
      },
    },
    footer: {
      padding: "14px 22px",
      borderTop: `1px solid ${border}`,
      background: surface,
      display: "flex",
      gap: 10,
      justifyContent: "flex-end",
    },
    cancelBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 600,
      padding: "8px 18px",
      color: textMuted,
      borderColor: border,
      "&:hover": {
        borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.14)",
        background: subtle,
      },
    },
    saveBtn: {
      position: "relative",
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 700,
      padding: "8px 22px",
      color: "#fff",
      background:
        "linear-gradient(135deg, #2ee37a 0%, #25d366 50%, #128c7e 100%)",
      boxShadow: "0 4px 14px rgba(18, 140, 126, 0.32)",
      "&:hover": {
        background:
          "linear-gradient(135deg, #2ee37a 0%, #1ec46a 50%, #0f7567 100%)",
      },
      "&.Mui-disabled": {
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        color: textMuted,
        boxShadow: "none",
      },
    },
    saveSpinner: {
      color: "#fff",
      position: "absolute",
      top: "50%",
      insetInlineStart: "50%",
      marginTop: -10,
      marginInlineStart: -10,
    },
  };
});

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email"),
  whatsappUsername: Yup.string()
    .nullable()
    .notRequired()
    .min(3, i18n.t("contactWhatsappUsername.validation.tooShort"))
    .max(35, i18n.t("contactWhatsappUsername.validation.tooLong"))
    .matches(
      /^[a-z0-9._]+$/,
      i18n.t("contactWhatsappUsername.validation.invalidChars")
    )
    .test(
      "no-www",
      i18n.t("contactWhatsappUsername.validation.noWww"),
      v => !v || !v.startsWith("www.")
    )
    .test(
      "no-domain",
      i18n.t("contactWhatsappUsername.validation.noDomain"),
      v => !v || !/\.[a-z]{2,}$/.test(v)
    ),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const initialState = {
    name: "",
    number: "",
    email: "",
    disableBot: false,
    whatsappUsername: "",
  };

  const [contact, setContact] = useState(initialState);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact(prev => ({ ...prev, ...initialValues }));
      }
      if (!contactId) return;
      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        setContact(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setContact(initialState);
  };

  const handleSaveContact = async values => {
    try {
      if (contactId) {
        await api.put(`/contacts/${contactId}`, values);
        handleClose();
      } else {
        const { data } = await api.post("/contacts", values);
        if (onSave) onSave(data);
        handleClose();
      }
      toast.success(i18n.t("contactModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.paper }}
      // scroll="paper" so the internal body div takes care of overflow
      // and the header/footer stay anchored top/bottom on long forms.
      scroll="paper"
    >
      <Formik
        initialValues={contact}
        enableReinitialize
        validationSchema={ContactSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveContact(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <div className={classes.header}>
              <div className={classes.titleRow}>
                <h2 className={classes.title}>
                  {contactId
                    ? i18n.t("contactModal.title.edit")
                    : i18n.t("contactModal.title.add")}
                </h2>
                <IconButton
                  onClick={handleClose}
                  className={classes.closeBtn}
                  aria-label="close"
                  size="small"
                >
                  <CloseRoundedIcon />
                </IconButton>
              </div>
            </div>

            <div className={classes.body}>
              {/* ── Contact information ─────────────────────────────── */}
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <PersonOutlineRoundedIcon />
                  <span className={classes.sectionLabel}>
                    {i18n.t("contactModal.form.mainInfo")}
                  </span>
                </div>
                <div className={classes.fieldStack}>
                  <Field
                    as={TextField}
                    label={i18n.t("contactModal.form.name")}
                    name="name"
                    autoFocus
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    className={classes.field}
                    fullWidth
                  />
                  <Field name="number">
                    {({ field, form }) =>
                      // BSUID (username-only) contacts store a synthetic wau_
                      // key in `number`; a phone input would garble it. Show a
                      // read-only, safe identifier instead — but keep the
                      // `number` value untouched in Formik so submit is unchanged.
                      isBsuidNumber(field.value) ? (
                        <TextField
                          value={
                            formatContactIdentifier({
                              number: field.value,
                              whatsappUsername: values?.whatsappUsername,
                            }).display
                          }
                          label={i18n.t("contactModal.form.number")}
                          variant="outlined"
                          className={classes.field}
                          fullWidth
                          disabled
                        />
                      ) : (
                        <PhoneInputWithCountry
                          value={field.value}
                          onChange={e =>
                            form.setFieldValue("number", e.target.value)
                          }
                          label={i18n.t("contactModal.form.number")}
                          error={errors.number}
                          touched={touched.number}
                          variant="outlined"
                          disabled={!!contactId}
                        />
                      )
                    }
                  </Field>
                  <Field
                    as={TextField}
                    label={i18n.t("contactModal.form.email")}
                    name="email"
                    type="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    placeholder="Email address"
                    fullWidth
                    variant="outlined"
                    className={classes.field}
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("contactWhatsappUsername.label")}
                    name="whatsappUsername"
                    error={
                      touched.whatsappUsername &&
                      Boolean(errors.whatsappUsername)
                    }
                    helperText={
                      (touched.whatsappUsername && errors.whatsappUsername) ||
                      i18n.t("contactWhatsappUsername.helper")
                    }
                    placeholder={i18n.t("contactWhatsappUsername.placeholder")}
                    fullWidth
                    variant="outlined"
                    className={classes.field}
                    InputProps={{
                      startAdornment: (
                        <span className={classes.usernamePrefix}>@</span>
                      ),
                    }}
                  />
                </div>
              </div>

              {/* ── Preferences toggle row ──────────────────────────── */}
              <div className={classes.section}>
                <div className={classes.toggleRow}>
                  <div className={classes.toggleLabel}>
                    <span className={classes.toggleTitle}>
                      {i18n.t("contactModal.form.disableBot")}
                    </span>
                  </div>
                  <Switch
                    size="small"
                    checked={values.disableBot}
                    onChange={() =>
                      setFieldValue("disableBot", !values.disableBot)
                    }
                    name="disableBot"
                    color="primary"
                  />
                </div>
              </div>

              {/* ── Additional information ─────────────────────────── */}
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <TuneRoundedIcon />
                  <span className={classes.sectionLabel}>
                    {i18n.t("contactModal.form.extraInfo")}
                  </span>
                </div>

                <FieldArray name="extraInfo">
                  {({ push, remove }) => (
                    <div className={classes.fieldStack}>
                      {values.extraInfo &&
                        values.extraInfo.length > 0 &&
                        values.extraInfo.map((info, index) => (
                          <div className={classes.extraRow} key={`${index}-info`}>
                            <Field
                              as={TextField}
                              label={i18n.t("contactModal.form.extraName")}
                              name={`extraInfo[${index}].name`}
                              variant="outlined"
                              className={`${classes.field} ${classes.extraField}`}
                            />
                            <Field
                              as={TextField}
                              label={i18n.t("contactModal.form.extraValue")}
                              name={`extraInfo[${index}].value`}
                              variant="outlined"
                              className={`${classes.field} ${classes.extraField}`}
                            />
                            <IconButton
                              size="small"
                              onClick={() => remove(index)}
                              className={classes.extraDeleteBtn}
                              aria-label="remove"
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </div>
                        ))}
                      <Button
                        variant="outlined"
                        onClick={() => push({ name: "", value: "" })}
                        className={classes.addExtraBtn}
                        startIcon={<AddRoundedIcon />}
                      >
                        {i18n.t("contactModal.buttons.addExtraInfo")}
                      </Button>
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>

            <div className={classes.footer}>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                variant="outlined"
                className={classes.cancelBtn}
              >
                {i18n.t("contactModal.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                className={classes.saveBtn}
              >
                {contactId
                  ? i18n.t("contactModal.buttons.okEdit")
                  : i18n.t("contactModal.buttons.okAdd")}
                {isSubmitting && (
                  <CircularProgress size={20} className={classes.saveSpinner} />
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ContactModal;
